import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SelectPoster = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userGoal, setUserGoal] = useState("");
  const [goals, setGoals] = useState({ goal1: "", goal2: "", goal3: "" });
  const navigate = useNavigate();

  useEffect(() => {
    const goalsData = localStorage.getItem("userGoals");
    if (!goalsData) {
      navigate("/");
      return;
    }
    const parsedGoals = JSON.parse(goalsData);
    setGoals(parsedGoals);
    const goalsArray = [parsedGoals.goal1, parsedGoals.goal2, parsedGoals.goal3].filter(g => g && g.trim());
    setUserGoal(goalsArray.join(" • "));

    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [navigate]);

  const handleSelectPoster = (productId) => {
    navigate(`/checkout/${productId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-2xl font-['Syne'] font-bold">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-8 border-b border-gray-200">
        <div className="max-w-[1800px] mx-auto flex justify-between items-center">
          <Link 
            to="/" 
            className="font-['Syne'] font-extrabold text-2xl tracking-tight"
            data-testid="logo-link"
          >
            CUE.PO
          </Link>
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-black transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="max-w-[1800px] mx-auto">
          {/* Your Goal */}
          <div className="mb-16 animate-fade-in-up">
            <p className="text-gray-500 text-sm uppercase tracking-wider mb-2">Your Goal</p>
            <h2 
              data-testid="user-goal-display"
              className="text-3xl md:text-4xl lg:text-5xl font-extrabold"
            >
              &ldquo;{userGoal}&rdquo;
            </h2>
          </div>

          {/* Section Title */}
          <div className="mb-12 animate-fade-in-up animation-delay-100">
            <h1 
              data-testid="select-heading"
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold"
            >
              SELECT YOUR SIZE
            </h1>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-16">
            {products.map((product, index) => (
              <div
                key={product.id}
                className={`product-card cursor-pointer group animate-fade-in-up animation-delay-${(index + 2) * 100}`}
                onClick={() => handleSelectPoster(product.id)}
                data-testid={`product-card-${product.id}`}
              >
                {/* White Poster Preview with User's Actual Goals */}
                <div className={`bg-white border border-gray-200 shadow-lg mb-6 flex flex-col items-center justify-between py-12 transition-transform group-hover:-translate-y-2 ${
                  product.id === 'small' ? 'aspect-[3/4]' : 
                  product.id === 'medium' ? 'aspect-[4/5]' : 
                  'aspect-[2/3]'
                }`}>
                  <div className="flex-1 flex flex-col items-center justify-center">
                    <p className="font-['Syne'] text-xl font-extrabold mb-6">Goals:</p>
                    <div className="space-y-4 text-center">
                      {goals.goal1 && <p className="font-['DM_Sans'] text-xl font-medium">1. {goals.goal1}</p>}
                      {goals.goal2 && <p className="font-['DM_Sans'] text-xl font-medium">2. {goals.goal2}</p>}
                      {goals.goal3 && <p className="font-['DM_Sans'] text-xl font-medium">3. {goals.goal3}</p>}
                    </div>
                  </div>
                  <span className="font-['Syne'] font-extrabold text-xs tracking-tight text-[#002FA7] opacity-30 mt-8">CUE.PO</span>
                </div>

                {/* Info */}
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 
                        className="text-2xl font-bold"
                        data-testid={`product-name-${product.id}`}
                      >
                        {product.name}
                      </h3>
                      <p className="text-gray-500">{product.size} &mdash; {product.dimensions}</p>
                    </div>
                    <span 
                      className="text-2xl font-bold"
                      data-testid={`product-price-${product.id}`}
                    >
                      ${product.price}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">{product.description}</p>
                  <Button
                    className="btn-primary w-full mt-4 group flex items-center justify-center gap-2"
                    data-testid={`select-btn-${product.id}`}
                  >
                    Select This Size
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SelectPoster;
